import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServerOrFail, loadRawEntityDataByID } from '@adornis/bh-util/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { map, of, switchMap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { BHPayment } from '../entities/BHPayment.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import {
  getEmptyParticipationStateStats,
  ParticipationDataview,
  ParticipationDataviewStateStat,
} from '../use-case-entities/ParticipationsDataview.js';
import { withIdTiebreaker } from '../util.js';

function getParticipationPrefilter(filter: BHFilter) {
  const conditions: Record<string, unknown>[] = [];
  const collectConditions = (mongoFilter: Record<string, unknown>) => {
    if (mongoFilter.productID !== undefined) conditions.push({ productID: mongoFilter.productID });
    if (mongoFilter['participation.state'] !== undefined)
      conditions.push({ state: mongoFilter['participation.state'] });

    // Only conjunctive constraints are safe to apply before the lookups.
    if (Array.isArray(mongoFilter.$and)) mongoFilter.$and.forEach(collectConditions);
  };

  collectConditions(filter.getMongoFilter());
  return conditions.length ? { $and: conditions } : {};
}

export const subscribeParticipationWorkshopDataview = registerSubscription({
  operationName: 'subscribeParticipationWorkshopDataview',
  type: () => [ParticipationDataview],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
      const productID = filter.productID;
      if (!productID) throw new Error('productID is required');

      gqlFields.participation ??= {};
      gqlFields.participation.productID = 1;
      gqlFields.participation.userID = 1;

      return of(null).pipe(
        switchMap(async () => {
          const product = await loadRawEntityDataByID(Product, productID, { _id: 1, institutionID: 1 });
          if (!product) throw new Error('product not found ' + filter.productID);
          return product.institutionID;
        }),
        switchMap(institutionID => watchEntireCollection(Participation._collectionName).pipe(map(() => institutionID))),
        switchMap(async institutionID => {
          const collection = mongo().collection<ParticipationWorkshop>('participations');

          if (!context.serverContext) {
            const user = await getCurrentUserServerOrFail();
            if (!user.hasPermission(Permission.MANAGE_PARTICIPATION, institutionID))
              throw new ExpectedError('no permission');
          }

          const userLookup = {
            from: User._collectionName,
            localField: 'participation.userID',
            foreignField: '_id',
            as: 'buyer',
          };

          const stammdatenLookup = (() => {
            const matchStammdaten = [
              { $eq: ['$userID', '$$localUserID'] }, //
              { $eq: ['$institutionID', institutionID] },
            ];

            return {
              from: UserStammdaten._collectionName,
              let: { localUserID: '$participation.userID' },
              pipeline: [{ $match: { $expr: { $and: matchStammdaten } } }],
              as: 'userStammdaten',
            };
          })();

          const currentPaymentLookup = {
            from: BHPayment._collectionName,
            localField: 'participation._id',
            foreignField: 'participationIDs',
            as: 'payment',
          };

          const sort = withIdTiebreaker(filter.getMongoOptions().sort, 'participation._id');

          const aggregation = [
            { $match: { _class: ParticipationWorkshop._class } },
            { $match: getParticipationPrefilter(filter) },
            { $replaceRoot: { newRoot: { participation: '$$ROOT' } } },

            // for filtering against product
            { $set: { productID: '$participation.productID' } },
            { $lookup: userLookup },
            { $addFields: { buyer: { $arrayElemAt: ['$buyer', 0] }, _class: ParticipationDataview._class } },
            { $lookup: stammdatenLookup },
            { $set: { 'buyer.stammdaten': { $arrayElemAt: ['$userStammdaten', 0] } } },
            { $lookup: currentPaymentLookup },
            { $match: filter.getMongoFilter() },
            { $sort: sort },
            { $skip: skip },
            { $limit: limit },
            { $project: gqlFields },
          ];

          const result = await collection.raw
            .aggregate<ParticipationDataview>(aggregation, { collation: { locale: 'de' } })
            .toArray();
          return result;
        }),
      );
    };
  },
});

export const subscribeParticipationEinzelberatungDataview = registerSubscription({
  operationName: 'subscribeParticipationEinzelberatungDataview',
  type: () => [ParticipationDataview],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
      return watchEntireCollection(Participation._collectionName).pipe(
        switchMap(async () => {
          const user = await getCurrentUserServerOrFail();
          if (!user.hasPermission(Permission.MANAGE_PARTICIPATION)) throw new ExpectedError('no permission');

          const collection = mongo().collection<Participation>(Participation._collectionName);
          const result = await collection.raw
            .aggregate<ParticipationDataview>(
              [
                { $match: { _class: ParticipationEinzelberatung._class } },
                { $match: getParticipationPrefilter(filter) },
                {
                  $replaceRoot: {
                    newRoot: {
                      participation: {
                        $arrayToObject: {
                          $map: {
                            input: { $objectToArray: '$$ROOT' },
                            as: 'field',
                            in: { k: '$$field.k', v: '$$field.v' },
                          },
                        },
                      },
                    },
                  },
                },
                {
                  $lookup: {
                    from: User._collectionName,
                    localField: 'participation.userID',
                    foreignField: '_id',
                    as: 'buyer',
                  },
                },
                { $addFields: { buyer: { $arrayElemAt: ['$buyer', 0] } } },
                {
                  $lookup: {
                    from: Product._collectionName,
                    localField: 'participation.productID',
                    foreignField: '_id',
                    as: 'product',
                  },
                },
                { $addFields: { product: { $arrayElemAt: ['$product', 0] } } },
                {
                  $addFields: {
                    slot: {
                      $filter: {
                        input: '$product.slots',
                        as: 'slot',
                        cond: { $eq: ['$$slot._id', '$participation.timeslotID'] },
                      },
                    },
                  },
                },
                { $addFields: { slot: { $arrayElemAt: ['$slot', 0] } } },
                {
                  $lookup: {
                    from: User._collectionName,
                    localField: 'slot.beraterID',
                    foreignField: '_id',
                    as: 'berater',
                  },
                },
                { $addFields: { berater: { $arrayElemAt: ['$berater', 0] } } },
                { $addFields: { _class: ParticipationDataview._class } },
                // for filtering against product
                { $set: { productID: '$participation.productID' } },
                { $match: filter.getMongoFilter() },
                { $sort: withIdTiebreaker(filter.getMongoOptions().sort, 'participation._id') },
                { $project: gqlFields },
                { $skip: skip },
                { $limit: limit },
              ],
              { collation: { locale: 'de' } },
            )
            .toArray();

          return result;
        }),
      );
    };
  },
});

export const subscribeParticipationsStatsByProductID = registerSubscription({
  operationName: 'subscribeParticipationsStatsByProductID',
  type: () => [ParticipationDataviewStateStat],
  params: [{ name: 'productID', type: () => String }],

  resolve: (productID: string) => (gqlFields: BaseQLSelectionSet<ParticipationDataview>) => {
    const findRecord = { productID: productID ?? 'NO_PRODUCT' };

    return watchEntireCollection(Participation._collectionName).pipe(
      switchMap(async () => {
        const collection = mongo().collection<Participation>(Participation._collectionName);

        const allStatesData = await await collection.raw
          .aggregate<{ _id: string; count: number }>([
            { $match: findRecord },
            { $group: { _id: '$state', count: { $sum: 1 } } },
          ])
          .toArray();

        const mappedStats = allStatesData.reduce((stats, curState) => {
          const curStateKey = curState._id;

          if (curState && stats[curStateKey]) {
            stats[curStateKey].ammount = curState.count;
          }

          return stats;
        }, getEmptyParticipationStateStats());

        return Object.values(mappedStats);
      }),
    );
  },
});
